import React, { useMemo } from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import WarningIcon from '@material-ui/icons/Warning';

// Data
import { numToTime } from '../../lib/helpers/conversion.helpers';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import getMessages from './ice-conflict-warning.messages';

// Components
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';

interface IIceConflictWarning {
  conflicts?: IIceSheetConflict[];
  fontSize?: 'inherit' | 'default' | 'small' | 'large';
}

export const describeConflictSlot = (conflict: IIceSheetConflict) =>
  `${conflict.date} ${numToTime(conflict.start)}–${numToTime(conflict.start + conflict.duration)}`;

// Two bookings on one sheet of ice cannot both run, so the grouping stage skips the later
// one. Rendered on the list row and the detail form from this single home.
export const IceConflictWarning: React.FC<IIceConflictWarning> = ({ conflicts, fontSize }) => {
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);

  if (!conflicts || !conflicts.length) return null;

  const title = <IntlMsg {...messages.tooltip} values={{ slots: conflicts.map(describeConflictSlot).join(', ') }} />;

  return (
    <Tooltip title={title}>
      <WarningIcon color="error" fontSize={fontSize || 'default'} />
    </Tooltip>
  );
};

// The same finding, spelled out on the detail page where there is room for it.
export const IceConflictBanner: React.FC<{ conflicts?: IIceSheetConflict[] }> = ({ conflicts }) => {
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);

  if (!conflicts || !conflicts.length) return null;

  const other = conflicts.length === 1
    ? <IntlMsg {...messages.otherBooking} />
    : <IntlMsg {...messages.otherBookings} values={{ count: conflicts.length }} />;

  return (
    <Box display="flex" alignItems="flex-start" mb={2}>
      <Box mr={1} display="flex"><WarningIcon color="error" /></Box>
      <Box>
        <Typography color="error" variant="subtitle2"><IntlMsg {...messages.bannerTitle} /></Typography>
        <Typography variant="body2">
          <IntlMsg {...messages.bannerBody} values={{ other, slots: conflicts.map(describeConflictSlot).join(', ') }} />
        </Typography>
      </Box>
    </Box>
  );
};
